import { type DetailedHTMLProps, type FunctionComponent, type MetaHTMLAttributes } from 'react';
import { Helmet } from 'react-helmet-async';
import { JsonLd } from 'react-schemaorg';
import { type WebSite, type SearchAction } from 'schema-dts';
import { SITE_NAME } from '../../../../constants/general';
import { DEFAULT_OPEN_GRAPH_IMAGE_ID, OPEN_GRAPH_IMAGE_OPTIONS } from '../../../../constants/images';
import { SITE_PATH } from '../../../../constants/links';
import { generateCloudinaryUrl } from '../../../../helpers/cloudinary-helper/cloudinary-helper';
import { replaceSiteName } from '../../../../helpers/site-helper/site.helper';
import { useIsLuxuryHeader, useIsLuxuryTheme } from '../../../../hooks/theme/theme.hooks';
import { type Breadcrumb, Breadcrumbs } from '../../../breadcrumbs/breadcrumbs.component';
import { UniversalLocationIndicator } from '../../../header-components/universal-location-indicator/universal-location-indicator.component';
import { PageStructuredData } from '../page-structured-data/page-structured-data.component';
import { breadcrumb } from './page-container.css';
import { isProductPage, type PageTypes } from './page-container.types';

export type MetaProps = DetailedHTMLProps<MetaHTMLAttributes<HTMLMetaElement>, HTMLMetaElement>;

export type PageContainerProps<T = {}> = {
	canonicalURL: string; // used to set the rel canonical
	children: JSX.Element; // contents of the page
	metaDescription: string; // used to populate the meta description
	pageTitle: string; // used to populate the title tag
	shouldBlockIndexing?: boolean; // used to control the meta robots directive
	automationHook?: string; // add data-automation tag for QA
	breadcrumbs?: Breadcrumb[];
	breadcrumbRowItem?: React.JSX.Element;
	showUpdateLocation?: boolean;
	layout?: 'minimized';
	showBorder?: boolean;
} & T;

type QueryAction = SearchAction & {
	'query-input': string;
};

/**
 * Accepts a pageTypeProps object and calls a typeguard function and returns an
 * array of meta tags specific to that page type.  As more page types are
 * defined this function will give an easy way to map those types to meta tags
 */
const pageSpecificMetaTags = (pageTypeProps: PageTypes) => {
	if (isProductPage(pageTypeProps)) {
		return [
			{ property: 'og:type', content: 'product' },
			{ property: 'og:image', content: generateCloudinaryUrl(pageTypeProps.imagePublicId, OPEN_GRAPH_IMAGE_OPTIONS) },
			{ property: 'product:price:currency', content: 'USD' },
			{ property: 'product:price:amount', content: `${pageTypeProps.price}` }
		];
	}
	return [
		{ property: 'og:type', content: 'website' },
		{
			property: 'og:image',
			content: generateCloudinaryUrl(DEFAULT_OPEN_GRAPH_IMAGE_ID, OPEN_GRAPH_IMAGE_OPTIONS)
		}
	];
};

const BreadcrumbSection: FunctionComponent<{ breadcrumbs: Breadcrumb[] | undefined; breadcrumbRowItem?: React.JSX.Element }> = ({
	breadcrumbs,
	breadcrumbRowItem
}) => (
	<div className="flex">
		{breadcrumbs && breadcrumbs.length ? (
			<div className={`flex flex-nowrap overflow-auto pr3 ${breadcrumb}`}>
				<div className="pl1 pl0-ns" style={{ flex: '0 0 auto' }}>
					<Breadcrumbs breadcrumbs={breadcrumbs} />
				</div>
			</div>
		) : null}
		{breadcrumbRowItem && <div className="self-center ml-auto dn db-ns">{breadcrumbRowItem}</div>}
	</div>
);

/**
 * Handles meta tag data including open graph tags as well as structured data json-ld scripts
 * Pass a PageType generic type when using to get proper typing for that page meta data
 * Example: <PageContainer<ProductPageType> ... />
 *
 * @returns properly styled page container with Helmet for consistent meta tags.
 */
export const PageContainer = <T extends PageTypes>({
	canonicalURL,
	children,
	metaDescription,
	pageTitle,
	automationHook,
	shouldBlockIndexing = false,
	layout,
	breadcrumbs,
	breadcrumbRowItem,
	showUpdateLocation = false,
	showBorder = false,
	...pageTypeProps
}: PageContainerProps<T>) => {
	const updatedPageTitle = replaceSiteName(pageTitle);
	const updatedDescription = replaceSiteName(metaDescription);

	const openGraphMetaTags = [
		{ property: 'og:title', content: updatedPageTitle },
		{ property: 'og:site_name', content: SITE_NAME },
		{ property: 'og:description', content: updatedDescription },
		...pageSpecificMetaTags(pageTypeProps)
	];
	if (canonicalURL) {
		openGraphMetaTags.push({ property: 'og:url', content: SITE_PATH + canonicalURL });
	}

	// sets up meta tags
	const metaTagsData: MetaProps[] = [
		{ charSet: 'utf-8' },
		{ name: 'description', content: updatedDescription },
		shouldBlockIndexing ? { name: 'robots', content: 'NOINDEX, NOFOLLOW' } : { name: 'robots', content: 'INDEX, FOLLOW' },
		...openGraphMetaTags
	].filter((tag) => tag !== null);

	// sets up link tags
	const linkTagsData = canonicalURL ? [{ rel: 'canonical', href: SITE_PATH + canonicalURL }] : undefined;

	const isHomePage = canonicalURL === '/';
	const homeAction: QueryAction = {
		'@type': 'SearchAction',
		target: SITE_PATH + '/search?term={searchTermString}',
		'query-input': 'required name=searchTermString'
	};

	// The logic with this line needs to be updated once we fully cut over to the new ferguson home.
	const containerStyle = layout === 'minimized' ? '' : ` bg-theme-white mb3 mt3-ns${showBorder ? ' ba-ns b--theme-grey-light' : ''}`;

	const isLuxuryHeader = useIsLuxuryHeader();
	const isLuxuryTheme = useIsLuxuryTheme();

	const isLuxury = isLuxuryHeader || isLuxuryTheme;

	return (
		<>
			<div className={`center-ns mw9-ns${containerStyle}`} data-automation={automationHook} data-testid="page-container">
				{breadcrumbs && (
					<div className="flex flex-column bg-theme-white pa0 pt3-ns ph3-ns theme-luxury:pl-2!">
						<BreadcrumbSection breadcrumbs={breadcrumbs} breadcrumbRowItem={breadcrumbRowItem} />
					</div>
				)}
				{showUpdateLocation && !isLuxury && (
					<div className={`tc theme-black pa2 dn-ns${breadcrumbs ? ' bt b--theme-grey-light' : ''}`}>
						<UniversalLocationIndicator />
					</div>
				)}
				<div className={`${layout === 'minimized' ? '' : 'pa2 pa3-ns'}`}>
					<Helmet title={updatedPageTitle} meta={metaTagsData} link={linkTagsData} htmlAttributes={{ lang: 'en' }} />
					{/* WebSite structured data for Google results Sitelinks search box
					 * https://developers.google.com/search/docs/advanced/structured-data/sitelinks-searchbox
					 */}
					{isHomePage && (
						<JsonLd<WebSite>
							item={{
								'@context': 'https://schema.org',
								'@type': 'WebSite',
								url: SITE_PATH,
								potentialAction: homeAction
							}}
						/>
					)}
					{/* Page specific structured data if applicable */}
					<PageStructuredData pageTypeProps={pageTypeProps} />
					{children}
				</div>
			</div>
		</>
	);
};
